import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, ShieldCheck, Gavel, FileCheck, CreditCard, Truck, Wrench, Globe2, MessageCircle, Calculator, ExternalLink, MapPin, Phone, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useLang } from "@/i18n";
import api, { fmtUSD } from "@/lib/api";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/1e41c489-be3d-415a-8975-b594e57798a1/images/b3f06fff7ebdb13cb791d2007b8ec2878587a623ff07effdc5a144337fe5e630.jpeg";
const SHIP_IMG = "https://images.unsplash.com/photo-1601311852860-1d8f42381551?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHw0fHxjYXIlMjBleHBvcnQlMjBzaGlwcGluZyUyMHBvcnQlMjBjb250YWluZXJ8ZW58MHx8fHwxNzg2NjMzODQ1fDA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const { t } = useLang();
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);
  const fee = settings?.flat_fee ?? 1000;
  const pct = settings?.deposit_pct ?? 10;
  const minDep = settings?.min_deposit ?? 400;
  const wa = settings?.whatsapp_number || "13055550123";

  return (
    <div>
      {/* HERO */}
      <section>
        <div className="relative h-[48vh] md:h-[60vh] overflow-hidden">
          <img src={HERO_IMG} alt="Subasta de vehículos en vivo" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F1A] via-[#0B0F1A]/25 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 -mt-36 md:-mt-44 pb-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-xs font-bold uppercase tracking-[0.2em] px-4 py-1.5 rounded-full mb-8 fade-up">
              <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.5} /> {t("Bróker licenciado de Copart")}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] fade-up" style={{ animationDelay: "80ms" }}>
              {t("Compra en subastas de")} <span className="text-[#2563EB]">Copart</span> {t("sin ser miembro.")}
            </h1>
            <p className="mt-6 text-base md:text-lg text-slate-300 leading-relaxed max-w-2xl fade-up" style={{ animationDelay: "160ms" }}>
              {t("Envíanos el lote, autoriza tu puja máxima y paga un depósito reembolsable. Nuestro equipo puja por ti y te acompaña con reparación, envío y exportación.")}
            </p>
            <div className="mt-10 flex flex-wrap gap-4 fade-up" style={{ animationDelay: "240ms" }}>
              <Link to="/registro">
                <Button data-testid="hero-cta" size="lg" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-8 h-12 text-base">
                  {t("Enviar mi lote")} <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                </Button>
              </Link>
              <a href={`https://wa.me/${wa}?text=${encodeURIComponent(t("Hola CarBid, quiero información sobre una subasta de Copart"))}`} target="_blank" rel="noreferrer">
                <Button data-testid="hero-whatsapp" size="lg" variant="outline" className="rounded-full px-8 h-12 text-base border-emerald-500/50 text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 hover:text-emerald-200">
                  <MessageCircle className="w-4 h-4 mr-2" strokeWidth={1.5} /> WhatsApp
                </Button>
              </a>
              <a href="https://www.copart.com" target="_blank" rel="noreferrer">
                <Button data-testid="hero-copart-link" size="lg" variant="outline" className="rounded-full px-8 h-12 text-base border-white/20 text-slate-200 bg-white/5 hover:bg-white/10 hover:text-white">
                  <ExternalLink className="w-4 h-4 mr-2" strokeWidth={1.5} /> Visitar Copart.com
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CÓMO FUNCIONA */}
      <section id="como-funciona" className="max-w-7xl mx-auto px-4 sm:px-8 py-20">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#2563EB] mb-3">{t("Proceso transparente")}</p>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-14">{t("Cómo funciona")}</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: FileCheck, title: t("1. Regístrate y sube tu ID"), d: t("Copart exige identificación (licencia o pasaporte). Súbela una vez desde tu perfil.") },
            { icon: Gavel, title: t("2. Envía el lote y autoriza tu puja"), d: t("Pega la URL o número de lote de Copart. Define tu puja máxima y acepta la comisión fija.") },
            { icon: CreditCard, title: t("3. Paga el depósito reembolsable"), d: t("{pct}% de tu puja máxima (mín. {min}). Si no ganamos, se te devuelve.", { pct, min: fmtUSD(minDep) }) },
            { icon: ShieldCheck, title: t("4. Pujamos por ti"), d: t("Nuestro equipo puja manualmente en nuestra cuenta de Copart hasta tu máximo autorizado.") },
            { icon: Truck, title: t("5. Gana, paga y recibe"), d: t("Ganamos: pagas el saldo por wire o Zelle. Perdemos: reembolso automático de tu depósito.") },
            { icon: Globe2, title: t("6. Servicios adicionales"), d: t("Estimados de reparación, transporte nacional y consultoría de exportación a tu país.") },
          ].map((s, i) => (
            <div key={i} className="card-tech rounded-xl p-8 fade-up" style={{ animationDelay: `${i * 80}ms` }} data-testid={`step-card-${i}`}>
              <s.icon className="w-8 h-8 text-[#2563EB] mb-5" strokeWidth={1.5} />
              <h3 className="text-xl font-semibold mb-2">{s.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{s.d}</p>
              {i === 0 && (
                <Link to="/registro" className="inline-block mt-5">
                  <Button data-testid="step-register-btn" size="sm" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-5">
                    {t("Crear cuenta")} <ArrowRight className="w-3.5 h-3.5 ml-1.5" strokeWidth={1.5} />
                  </Button>
                </Link>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* TARIFAS */}
      <section id="tarifas" className="border-y border-white/10 bg-[#111827]/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#2563EB] mb-3">{t("Sin sorpresas")}</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-6">{t("Tarifas claras, por escrito")}</h2>
            <ul className="space-y-4 text-sm text-slate-300">
              <li className="flex gap-3"><Calculator className="w-5 h-5 text-[#2563EB] shrink-0" strokeWidth={1.5} />
                <span><strong className="text-white">{t("Comisión de bróker: {fee}", { fee: fmtUSD(fee) })}</strong>{t(" — tarifa plana por vehículo, solo si ganamos tu subasta.")}</span></li>
              <li className="flex gap-3"><CreditCard className="w-5 h-5 text-[#2563EB] shrink-0" strokeWidth={1.5} />
                <span><strong className="text-white">{t("Depósito: {pct}% de tu puja máxima (mín. {min})", { pct, min: fmtUSD(minDep) })}</strong>{t(" — 100% reembolsable si no ganamos.")}</span></li>
              <li className="flex gap-3"><ShieldCheck className="w-5 h-5 text-[#22C55E] shrink-0" strokeWidth={1.5} />
                <span><strong className="text-white">{t("Reembolso:")}</strong>{t(" se descuenta solo la cuota de procesamiento de tarjeta: {us}% tarjetas de EE.UU. / {foreign}% tarjetas extranjeras.", { us: settings?.refund_fee_us ?? 3.5, foreign: settings?.refund_fee_foreign ?? 4.5 })}</span></li>
              <li className="flex gap-3"><Truck className="w-5 h-5 text-[#2563EB] shrink-0" strokeWidth={1.5} />
                <span><strong className="text-white">{t("Estimador de costos:")}</strong>{t(" vehículo + comisión + impuestos estimados + transporte, antes de pujar.")}</span></li>
            </ul>
          </div>
          <div className="relative">
            <img src={SHIP_IMG} alt="Exportación de vehículos" className="rounded-xl border border-white/10 object-cover w-full max-h-[80vh] md:max-h-none" />
            <div className="absolute -bottom-5 -left-5 bg-[#0B0F1A] border border-emerald-500/40 rounded-xl p-5 shadow-2xl">
              <p className="text-xs text-slate-400 uppercase tracking-widest mb-1">{t("Depósito reembolsable")}</p>
              <p className="font-display text-2xl font-extrabold text-[#22C55E]">{t("Si no ganamos, te devolvemos tu dinero")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICIOS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-20">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-14">{t("Más que pujas")}</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Wrench, title: t("Estimado de reparación"), d: t("Talleres aliados evalúan tu vehículo salvage y te cotizan la reparación antes de que decidas.") },
            { icon: Truck, title: t("Envío nacional"), d: t("Coordinamos el transporte desde el patio de Copart hasta tu dirección o puerto.") },
            { icon: Globe2, title: t("Exportación"), d: t("Documentación, título, aduana y contenedor. Te asesoramos de puerta a puerto.") },
          ].map((s, i) => (
            <div key={i} className="card-tech rounded-xl p-8" data-testid={`service-card-${i}`}>
              <s.icon className="w-8 h-8 text-[#22C55E] mb-5" strokeWidth={1.5} />
              <h3 className="text-xl font-semibold mb-2">{s.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed mb-6">{s.d}</p>
              <a href={`https://wa.me/${wa}?text=${encodeURIComponent(`${t("Hola CarBid, me interesa:")} ${s.title}`)}`} target="_blank" rel="noreferrer"
                className="text-sm text-emerald-400 hover:text-emerald-300 transition-colors inline-flex items-center gap-1.5">
                <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> {t("Consultar por WhatsApp")}
              </a>
            </div>
          ))}
        </div>
      </section>

      {/* VEHÍCULOS DESTACADOS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-20">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#2563EB] mb-3">{t("Inventario de subastas")}</p>
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-xl">
            {t("Vehículos premium que nuestros clientes ganan en Copart")}
          </h2>
          <p className="text-sm text-slate-400 max-w-sm leading-relaxed">
            {t("SUVs y camionetas de lujo a precios muy por debajo del mercado. Ejemplos del inventario típico de las subastas.")}
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-6" data-testid="showcase-grid">
          {[
            { name: "Mercedes-Benz G-Class", tag: "SUV de lujo", img: "https://images.unsplash.com/photo-1648413653819-7c0fd93e8e6a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxNZXJjZWRlcyUyMEclMjBXYWdvbiUyMGx1eHVyeSUyMFNVVnxlbnwwfHx8fDE3ODY2MzcwNTZ8MA&ixlib=rb-4.1.0&q=85", big: true },
            { name: "BMW M Series", tag: "Deportivo premium", img: "https://images.unsplash.com/photo-1607853554439-0069ec0f29b6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxCTVclMjBsdXh1cnklMjBjYXJ8ZW58MHx8fHwxNzg2NjM3MDU2fDA&ixlib=rb-4.1.0&q=85" },
            { name: "Toyota 4Runner", tag: "El favorito para exportar", img: "https://images.unsplash.com/photo-1773370526433-0614dea56cc9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwzfHxUb3lvdGElMjA0UnVubmVyJTIwb2ZmJTIwcm9hZCUyMFNVVnxlbnwwfHx8fDE3ODY2MzcwNTZ8MA&ixlib=rb-4.1.0&q=85" },
            { name: "Lexus GX", tag: "SUV de lujo", img: "https://images.unsplash.com/photo-1779983625011-e9c207710d11?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxMZXh1cyUyMFNVViUyMGx1eHVyeXxlbnwwfHx8fDE3ODY2MzcwNTZ8MA&ixlib=rb-4.1.0&q=85" },
          ].map((v, i) => (
            <Link key={i} to="/registro" data-testid={`showcase-card-${i}`}
              className={`group relative rounded-xl overflow-hidden border border-white/10 fade-up ${v.big ? "md:col-span-2 md:row-span-2" : ""}`}
              style={{ animationDelay: `${i * 100}ms` }}>
              <img src={v.img} alt={v.name}
                className={`w-full object-cover transition-transform duration-500 group-hover:scale-105 ${v.big ? "h-72 md:h-full" : "h-64"}`} />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F1A] via-[#0B0F1A]/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 flex items-end justify-between">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#22C55E] mb-1">{t(v.tag)}</p>
                  <p className="font-display text-xl font-bold text-white">{v.name}</p>
                </div>
                <span className="text-xs text-blue-300 bg-[#2563EB]/20 border border-[#2563EB]/40 rounded-full px-3 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
                  {t("Pujar ahora")}
                </span>
              </div>
            </Link>
          ))}
        </div>
        <p className="text-[11px] text-slate-500 mt-4">{t("Imágenes referenciales del tipo de vehículos disponibles en subastas Copart.")}</p>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-4 sm:px-8 py-20">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-10 text-center">{t("Preguntas frecuentes")}</h2>
        <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
          {[
            [t("¿Necesito licencia de dealer para comprar en Copart?"), t("No. CarBid puja con su propia cuenta licenciada de bróker, así que puedes comprar vehículos salvage y usados sin licencia propia.")],
            [t("¿Qué pasa con mi depósito si no ganan la subasta?"), t("Te lo devolvemos automáticamente a tu tarjeta. Solo se descuenta la cuota de procesamiento que cobra la red de tarjetas (3.5% tarjetas de EE.UU. / 4.5% extranjeras), divulgada antes de pagar.")],
            [t("¿Cómo pago el saldo si gano?"), t("Por transferencia wire o Zelle. Nuestro equipo confirma la recepción y tu solicitud pasa a 'Pagado'. Desde ahí coordinamos título, transporte y exportación.")],
            [t("¿Qué pasa si la página de Copart no carga los datos del lote?"), t("Nada se rompe: el auto-rellenado es solo una conveniencia. Siempre puedes ingresar los datos del vehículo manualmente y nuestro equipo puede corregirlos después.")],
            [t("¿Puedo pujar por más de un vehículo?"), t("Sí. Cada lote se rastrea de forma independiente con su propio depósito, estado e historial.")],
          ].map(([q, a], i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="card-tech rounded-xl border-white/10 px-6" data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-left text-sm font-semibold hover:text-[#2563EB] transition-colors">{q}</AccordionTrigger>
              <AccordionContent className="text-sm text-slate-400 leading-relaxed">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* CONTACTO */}
      <section id="contacto" className="max-w-7xl mx-auto px-4 sm:px-8 pb-20">
        <div className="card-tech tracing-beam rounded-xl p-8 md:p-12 grid md:grid-cols-2 gap-8 items-center" data-testid="contact-section">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#2563EB] mb-3">{t("Contacto")}</p>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-6">{t("¿Dudas? Habla con un bróker")}</h2>
            <div className="space-y-4 text-sm text-slate-300">
              <p className="flex items-start gap-3" data-testid="contact-address">
                <MapPin className="w-5 h-5 text-[#2563EB] shrink-0" strokeWidth={1.5} />
                19394 SW 106 Ave, Miami, FL 33157
              </p>
              <a href="tel:+17868773988" data-testid="contact-phone"
                className="flex items-center gap-3 hover:text-white transition-colors">
                <Phone className="w-5 h-5 text-[#2563EB] shrink-0" strokeWidth={1.5} />
                786-877-3988
              </a>
              <a href={`mailto:${settings?.contact_email || "carbidmiami@gmail.com"}`} data-testid="contact-email"
                className="flex items-center gap-3 hover:text-white transition-colors">
                <Mail className="w-5 h-5 text-[#2563EB] shrink-0" strokeWidth={1.5} />
                {settings?.contact_email || "carbidmiami@gmail.com"}
              </a>
            </div>
          </div>
          <div className="flex md:justify-end">
            <a href={`https://wa.me/${wa}?text=${encodeURIComponent(t("Hola CarBid, tengo una consulta"))}`} target="_blank" rel="noreferrer">
              <Button data-testid="contact-whatsapp" size="lg"
                className="rounded-full px-8 h-12 text-base bg-[#22C55E] hover:bg-[#16A34A] text-white">
                <MessageCircle className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t("Escríbenos por WhatsApp")}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-24 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-4">{t("¿Listo para pujar?")}</h2>
          <p className="text-slate-400 text-base mb-8 max-w-xl mx-auto">{t("Crea tu cuenta en 2 minutos, sube tu ID y envíanos tu primer lote de Copart.")}</p>
          <Link to="/registro">
            <Button data-testid="footer-cta" size="lg" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-10 h-12 text-base">
              {t("Crear cuenta gratis")} <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
